import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  ClipboardList, Download, FileSpreadsheet, Calendar,
  Building2, Layers, Wallet, Receipt, CreditCard,
} from 'lucide-react';
import { maskDoc } from '../lib/cpfCnpj';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtBRL = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);

const formatDateBR = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('pt-BR'); }
  catch (_e) { return iso; }
};

const Section = ({ icon: Icon, title, subtitle, children, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="pt-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-emerald-700" />
        <div>
          <h3 className="font-bold text-sm">{title}</h3>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      {children}
    </CardContent>
  </Card>
);

export const FinanceiroPrestacaoPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [filters, setFilters] = useState({ data_inicio: '', data_fim: '' });
  const [pending, setPending] = useState({ data_inicio: '', data_fim: '' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.data_inicio) params.data_inicio = filters.data_inicio;
      if (filters.data_fim) params.data_fim = filters.data_fim;
      const res = await axios.get(`${BACKEND_URL}/api/financeiro/prestacao`, {
        params, withCredentials: true,
      });
      setData(res.data);
    } catch (_err) {
      toast.error('Erro ao carregar prestação de contas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (filters.data_inicio) params.set('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.set('data_fim', filters.data_fim);
      const url = `${BACKEND_URL}/api/financeiro/prestacao/export.xlsx?${params.toString()}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `prestacao-contas-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success('Arquivo gerado.');
    } catch (err) {
      toast.error('Falha ao gerar arquivo: ' + (err.message || ''));
    } finally {
      setDownloading(false);
    }
  };

  if (loading || !data) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </Layout>
    );
  }

  const saldo = Math.max(0, (data.valor_total_orcamento || 0) - (data.total_pago || 0));
  const pct = data.valor_total_orcamento > 0
    ? Math.round((data.total_pago / data.valor_total_orcamento) * 100)
    : 0;

  return (
    <Layout>
      <div className="space-y-6" data-testid="financeiro-prestacao-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
              <ClipboardList className="h-6 w-6 text-emerald-700" />
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight">Prestação de Contas</h1>
              <p className="text-muted-foreground mt-1 max-w-2xl">
                Relatório consolidado nos moldes eleitorais. Exporte em Excel para envio ao
                contador ou anexar ao SPCE/TSE.
              </p>
            </div>
          </div>
          <Button onClick={downloadXlsx} disabled={downloading} data-testid="export-xlsx-btn">
            {downloading ? (
              <>
                <FileSpreadsheet className="h-4 w-4 mr-2 animate-pulse" /> Gerando...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" /> Exportar XLSX
              </>
            )}
          </Button>
        </div>

        {/* Filtros */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div>
                <Label className="text-xs text-muted-foreground">Data inicial</Label>
                <Input
                  type="date"
                  value={pending.data_inicio}
                  onChange={(e) => setPending({ ...pending, data_inicio: e.target.value })}
                  data-testid="prestacao-data-inicio"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Data final</Label>
                <Input
                  type="date"
                  value={pending.data_fim}
                  onChange={(e) => setPending({ ...pending, data_fim: e.target.value })}
                  data-testid="prestacao-data-fim"
                />
              </div>
              <Button onClick={() => setFilters(pending)} data-testid="apply-prestacao-filter">
                Aplicar filtro
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setPending({ data_inicio: '', data_fim: '' }); setFilters({ data_inicio: '', data_fim: '' }); }}
              >
                Limpar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Resumo KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPICard icon={Wallet} label="Orçamento" value={fmtBRL(data.valor_total_orcamento)} sub={data.plan?.name} />
          <KPICard icon={Receipt} label="Pago" value={fmtBRL(data.total_pago)} sub={`${pct}% consumido`} />
          <KPICard icon={Wallet} label="Saldo" value={fmtBRL(saldo)} accent="text-emerald-600" />
          <KPICard icon={ClipboardList} label="Lançamentos" value={data.count_lancamentos} />
        </div>

        {/* Por Área (com subáreas) */}
        <Section icon={Layers} title="Resumo por área" subtitle="Totais consolidados com detalhamento de subáreas">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-xs uppercase text-muted-foreground">
                <tr className="text-left">
                  <th className="py-2 px-2">Área / Subárea</th>
                  <th className="py-2 px-2 text-right">Lançamentos</th>
                  <th className="py-2 px-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.by_area?.length === 0 && (
                  <tr><td colSpan="3" className="py-4 text-center text-muted-foreground">Sem dados.</td></tr>
                )}
                {(data.by_area || []).map((a) => (
                  <React.Fragment key={a.area_id}>
                    <tr className="bg-muted/30 font-bold">
                      <td className="py-2 px-2">
                        <span className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full" style={{ background: a.area_cor || '#94a3b8' }} />
                          {a.area_name}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right">{a.count}</td>
                      <td className="py-2 px-2 text-right">{fmtBRL(a.total)}</td>
                    </tr>
                    {(a.subareas || []).map((s) => (
                      <tr key={s.subarea_id} className="border-b">
                        <td className="py-2 px-2 pl-8 text-muted-foreground">↳ {s.subarea_name}</td>
                        <td className="py-2 px-2 text-right text-muted-foreground">{s.count}</td>
                        <td className="py-2 px-2 text-right">{fmtBRL(s.total)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Por Período */}
        <Section icon={Calendar} title="Por período">
          <Table3
            columns={['Período', 'Lançamentos', 'Total']}
            rows={(data.by_period || []).map((p) => [p.name, p.count, fmtBRL(p.total)])}
          />
        </Section>

        {/* Por Fornecedor */}
        <Section icon={Building2} title="Por fornecedor" subtitle="Inclui CPF/CNPJ formatado">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-xs uppercase text-muted-foreground">
                <tr className="text-left">
                  <th className="py-2 px-2">Fornecedor</th>
                  <th className="py-2 px-2">Tipo</th>
                  <th className="py-2 px-2">Documento</th>
                  <th className="py-2 px-2 text-right">Lanç.</th>
                  <th className="py-2 px-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(data.by_supplier || []).length === 0 && (
                  <tr><td colSpan="5" className="py-4 text-center text-muted-foreground">Sem dados.</td></tr>
                )}
                {(data.by_supplier || []).map((s, idx) => (
                  <tr key={`${s.name}-${idx}`} className="border-b">
                    <td className="py-2 px-2 font-medium">{s.name}</td>
                    <td className="py-2 px-2">
                      {s.tipo === 'pj' ? <Badge variant="outline">PJ</Badge>
                        : s.tipo === 'pf' ? <Badge variant="outline">PF</Badge> : '—'}
                    </td>
                    <td className="py-2 px-2 font-mono text-xs">
                      {s.doc ? maskDoc(s.doc, s.tipo) : '—'}
                    </td>
                    <td className="py-2 px-2 text-right text-muted-foreground">{s.count}</td>
                    <td className="py-2 px-2 text-right font-bold">{fmtBRL(s.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* Por Forma de pagamento */}
        <Section icon={CreditCard} title="Por forma de pagamento">
          <Table3
            columns={['Forma', 'Lançamentos', 'Total']}
            rows={(data.by_payment_method || []).map((m) => [m.label, m.count, fmtBRL(m.total)])}
          />
        </Section>

        {/* Detalhado */}
        <Section
          icon={Receipt}
          title="Detalhamento dos lançamentos"
          subtitle={
            data.detalhe_count > 500
              ? `Mostrando os primeiros 500 de ${data.detalhe_count}. Exporte XLSX para o relatório completo.`
              : `${data.detalhe_count} lançamento(s) no período.`
          }
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-xs uppercase text-muted-foreground">
                <tr className="text-left">
                  <th className="py-2 px-2">Data</th>
                  <th className="py-2 px-2">Descrição</th>
                  <th className="py-2 px-2">Fornecedor</th>
                  <th className="py-2 px-2">Área</th>
                  <th className="py-2 px-2 text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {(data.detalhe || []).length === 0 && (
                  <tr><td colSpan="5" className="py-4 text-center text-muted-foreground">Sem lançamentos no período.</td></tr>
                )}
                {(data.detalhe || []).map((e) => (
                  <tr key={e.id} className="border-b">
                    <td className="py-2 px-2 text-xs">{formatDateBR(e.data_pagamento)}</td>
                    <td className="py-2 px-2">{e.descricao}</td>
                    <td className="py-2 px-2">
                      <div>{e.fornecedor || '—'}</div>
                      {e.fornecedor_documento && (
                        <div className="text-[11px] text-muted-foreground font-mono">
                          {(e.fornecedor_tipo === 'pj' ? 'CNPJ ' : 'CPF ')}
                          {maskDoc(e.fornecedor_documento, e.fornecedor_tipo)}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-2 text-xs">
                      {e._area_name || '—'}
                      {e._subarea_name && (
                        <div className="text-muted-foreground">{e._subarea_name}</div>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right font-bold">{fmtBRL(e.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </Layout>
  );
};

const KPICard = ({ icon: Icon, label, value, sub, accent }) => (
  <Card>
    <CardContent className="pt-5">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className={`text-xl font-bold mt-1 truncate ${accent || ''}`}>{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1 truncate">{sub}</p>}
        </div>
        <Icon className="h-5 w-5 text-primary shrink-0" />
      </div>
    </CardContent>
  </Card>
);

const Table3 = ({ columns, rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="border-b text-xs uppercase text-muted-foreground">
        <tr className="text-left">
          {columns.map((c, i) => (
            <th key={i} className={`py-2 px-2 ${i > 0 ? 'text-right' : ''}`}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={columns.length} className="py-4 text-center text-muted-foreground">Sem dados.</td></tr>
        )}
        {rows.map((r, i) => (
          <tr key={i} className="border-b">
            {r.map((cell, j) => (
              <td key={j} className={`py-2 px-2 ${j > 0 ? 'text-right' : ''} ${j === 0 ? 'font-medium' : ''}`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
